import type { EChartsOption } from 'echarts';

interface ChartSeriesInput {
  data: Array<{ timestamp: string; gas_conc?: number | null; temperature: number }>;
  unitName: string;
}

interface ChartOptionParams extends ChartSeriesInput {
  isMultiDayRange: boolean;
  title?: string;
  theme?: any;
}

function sampleData<T>(data: T[], maxPoints: number = 2000): T[] {
  if (data.length <= maxPoints) return data;

  const step = Math.ceil(data.length / maxPoints);
  const sampled: T[] = [];

  for (let i = 0; i < data.length; i += step) {
    sampled.push(data[i]);
  }

  if (sampled[sampled.length - 1] !== data[data.length - 1]) {
    sampled.push(data[data.length - 1]);
  }

  return sampled;
}

function formatAxisLabel(value: number, isMultiDayRange: boolean): string {
  const date = new Date(value);
  if (isMultiDayRange) {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hour = date.getHours().toString().padStart(2, '0');
    const min = date.getMinutes().toString().padStart(2, '0');
    return `${month}-${day} ${hour}:${min}`;
  }
  const hour = date.getHours().toString().padStart(2, '0');
  const min = date.getMinutes().toString().padStart(2, '0');
  return `${hour}:${min}`;
}

function buildSeries(params: ChartSeriesInput): any[] {
  const { data, unitName } = params;
  const commonLineProps = {
    step: 'end' as const,
    symbol: 'circle',
    smooth: false,
    sampling: 'lttb',
    large: true,
    largeThreshold: 500,
  };

  const series: any[] = [
    {
      name: unitName === 'ppm' ? 'Gas Conc' : `Conc. (${unitName})`,
      type: 'line',
      data: data.map(r => [r.timestamp, r.gas_conc]),
      symbolSize: 4,
      // 센서가 disabled/poisoned였던 구간은 gas_conc가 null로 내려옴 - 선을 이어붙이지 않고 끊어서 표시
      connectNulls: false,
      ...commonLineProps,
    },
    {
      name: 'Temperature',
      type: 'line',
      yAxisIndex: 1,
      data: data.map(r => [r.timestamp, r.temperature]),
      lineStyle: { type: 'dashed' },
      symbolSize: 3,
      connectNulls: true,
      ...commonLineProps,
    },
  ];

  return series;
}

export function createChartOption(params: ChartOptionParams): EChartsOption {
  const { data, unitName, isMultiDayRange, title, theme } = params;
  const sampledData = sampleData(data, 10000);

  if (sampledData.length === 0) return {};

  const series = buildSeries({ data: sampledData, unitName });

  const dataCount = data.length;
  const initialDisplayCount = 100;
  const zoomStart =
    dataCount > initialDisplayCount
      ? Math.max(0, ((dataCount - initialDisplayCount) / dataCount) * 100)
      : 0;

  const textColor = theme?.colors.text.primary;
  const axisLineColor = theme?.colors.border.container;
  const splitLineColor = theme?.colors.border.divider;

  const yAxisNameStyle = theme
    ? { fontSize: theme.fontSize.md, fontWeight: theme.fontWeight.semibold, color: textColor }
    : undefined;

  const axisLabelStyle = textColor ? { color: textColor, fontSize: 11 } : undefined;
  const axisLineStyle = axisLineColor ? { lineStyle: { color: axisLineColor } } : undefined;
  const splitLineStyle = splitLineColor
    ? { lineStyle: { color: splitLineColor, type: 'dashed' as const } }
    : undefined;

  return {
    backgroundColor: 'transparent',
    animation: sampledData.length <= 500,
    ...(title
      ? {
          title: {
            text: title,
            left: 'center',
            textStyle: { fontSize: 14, color: textColor },
          },
        }
      : {}),
    tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
    legend: {
      top: title ? 30 : 10,
      left: 'center',
      ...(textColor ? { textStyle: { color: textColor } } : {}),
    },
    grid: {
      ...(title
        ? { top: 100, left: '10px', right: '50px', bottom: '18%', containLabel: true }
        : { left: '3%', right: '10%', bottom: '18%', containLabel: true }),
    },
    xAxis: {
      type: 'time',
      axisLabel: {
        rotate: 45,
        formatter: (value: number) => formatAxisLabel(value, isMultiDayRange),
        ...(axisLabelStyle || {}),
      },
      ...(axisLineStyle ? { axisLine: axisLineStyle, axisTick: axisLineStyle } : {}),
      splitLine: { show: false },
    },
    yAxis: [
      {
        type: 'value',
        name: unitName,
        ...(yAxisNameStyle ? { nameTextStyle: yAxisNameStyle } : {}),
        ...(axisLabelStyle ? { axisLabel: axisLabelStyle } : {}),
        ...(axisLineStyle ? { axisLine: { show: true, ...axisLineStyle }, axisTick: axisLineStyle } : {}),
        ...(splitLineStyle ? { splitLine: splitLineStyle } : {}),
      },
      {
        type: 'value',
        name: '°C',
        position: 'right',
        ...(yAxisNameStyle ? { nameTextStyle: yAxisNameStyle } : {}),
        ...(axisLabelStyle ? { axisLabel: axisLabelStyle } : {}),
        ...(axisLineStyle ? { axisLine: { show: true, ...axisLineStyle }, axisTick: axisLineStyle } : {}),
        splitLine: { show: false },
      },
    ],
    dataZoom: [
      {
        type: 'slider',
        show: true,
        xAxisIndex: 0,
        start: zoomStart,
        end: 100,
        height: 30,
        bottom: '5%',
        labelFormatter: (value: number) => formatAxisLabel(value, isMultiDayRange),
      },
      { type: 'inside', xAxisIndex: 0, start: zoomStart, end: 100 },
    ],
    toolbox: {
      itemSize: 16,
      feature: {
        restore: { show: true, title: 'Restore' },
        saveAsImage: { show: true, title: 'Save as Image', name: 'chart', pixelRatio: 2 },
      },
      right: title ? '0' : '1%',
      top: '5%',
    },
    series,
  };
}
